import _ from 'underscore';
import Cldr from 'cldrjs';
import { useCallback, useContext, useMemo } from 'react';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { Constants } from '@bingads-webui-clientcenter/entity-utils';
import { BundleCouponClassCodes } from '@bingads-webui-campaign-react/promotion-utils';

import { DefaultCurrencyCode, getCouponTerms, couponTypes, formatCost } from '../helpers';

export function getCldrAttributes(locale) {
  const cldr = new Cldr(locale);
  return cldr.attributes;
}

export function useCouponBannerInfo({ coupon, i18n, getCouponCurrencyCode = _.noop }) {
  const {
    couponDetails,
    couponValue,
    isCouponExpired,
  } = coupon || {};

  const serverContext = useContext(ServerContext);
  const locale = serverContext.CurrentLocale || document.documentElement.lang;
  const { language } = getCldrAttributes(locale);

  const canUseCoupon = (!_.isEmpty(coupon) &&
    !_.isEmpty(couponDetails) &&
    !isCouponExpired);

  const couponSpendRestriction = useMemo(() => {
    if (canUseCoupon) {
      if (couponDetails.CouponPurposeId === Constants.CouponPurposeId.StoreAds) {
        return i18n.getString(_TL_(' This offer only applies to the amount you spend on your Microsoft Store ads campaigns.'));
      }

      if (couponDetails.CouponPurposeId === Constants.CouponPurposeId.MultiChannels) {
        return '';
      }

      if (couponDetails.CouponPurposeId === Constants.CouponPurposeId.MSANSignUp) {
        return i18n.getString(_TL_(' Offer applies only towards spend on audience campaigns.'));
      }

      return i18n.getString(_TL_(' This offer only applies to ads on the Microsoft Advertising Network.'));
    }

    return '';
  }, [canUseCoupon, couponDetails, i18n]);

  const getCouponBannerTemplate = useCallback(() => {
    let couponBannerText = '';
    const termsApplyString = i18n.getString(_TL_('Terms Apply'));
    const currency = getCouponCurrencyCode(couponDetails.CurrencyId) || DefaultCurrencyCode;
    const couponAmount = formatCost(couponDetails.CouponValue, i18n, currency);

    switch (couponDetails.CouponTypeId) {
      case couponTypes.Percent:
        couponBannerText = i18n.getString(
          _TL_('First time sign ups get {{ percentOff }}% off on all spend up to {{ couponAmount }}.'),
          {
            percentOff: couponDetails.PercentOff,
            couponAmount,
          }
        );
        break;
      case couponTypes.Performance:
        couponBannerText = i18n.getString(_TL_('First time sign ups get {{ couponAmount }} when you spend {{ upfrontSpendAmount }}.'), {
          couponAmount,
          upfrontSpendAmount: formatCost(couponDetails.UpfrontSpending, i18n, currency),
        });
        break;
      default:
        couponBannerText = i18n.getString(_TL_('First time sign ups get bonus of {{ couponAmount }}.'), { couponAmount });
    }

    // overwrite if met the requirement
    if (_.include(BundleCouponClassCodes, couponDetails.ClassName)) {
      couponBannerText = i18n.getString(_TL_('New customers can get {{ couponAmount }} for every {{ upfrontSpendAmount }} you spend, with a maximum discount of {{maxValueAmount}}.'), {
        couponAmount,
        upfrontSpendAmount: formatCost(couponDetails.UpfrontSpending, i18n, currency),
        maxValueAmount: formatCost(500, i18n, currency),
      });
    }

    const templateText = `${couponBannerText}${couponSpendRestriction} {{link}}${termsApplyString}{{/link}}`;

    if (couponValue) {
      return {
        coupon: couponValue.couponString,
        class: 'signup-coupon-banner',
        href: getCouponTerms(couponValue.couponString, language),
        template: templateText,
      };
    }
    return null;
  }, [i18n, getCouponCurrencyCode, couponDetails, couponSpendRestriction, couponValue, language]);

  const bannerInfo = useMemo(() => {
    if (canUseCoupon) {
      const couponInfo = getCouponBannerTemplate(i18n, couponDetails, couponValue);
      return couponInfo;
    }
    return null;
  }, [canUseCoupon, couponDetails, couponValue, getCouponBannerTemplate, i18n]);

  return bannerInfo;
}
